import { useId, useRef } from 'react'
import type { JSX } from 'react'

import { NotesVisibilityHint } from '~publish/components/PostNotes/NotesVisibilityHint'
import { CreatePostNoteForm } from '~publish/components/PostNotes/CreatePostNoteForm'
import { FocusAfterDelete } from '~publish/components/PostNotes/FocusAfterDelete'
import { PostNotesFeed } from '~publish/components/PostNotes/PostNotesFeed'
import { NotesFeedActions } from '~publish/components/PostNotes/NotesFeedActions'
import {
  PostNotesProvider,
  usePostNotesContext,
} from '~publish/components/PostNotes/PostNotesProvider'
import { usePostNotesFrame } from '~publish/components/PostNotes/usePostNotesFrame'
import { SplitTagBadge } from '~publish/components/SplitTagBadge/SplitTagBadge'
import type { PostAction } from '~publish/utils/permissions/graphqlEnums'

import { COLLABORATION_NOTES_SPLIT } from './useCollaborationNotesPanel'

import styles from './PostDetailsNotes.module.css'

export function PostDetailsNotes({
  postId,
  allowedActions = [],
  id,
  hidden,
  channelId,
  focusComposer = false,
}: {
  postId: string
  allowedActions?: readonly PostAction[]
  id?: string
  hidden?: boolean
  channelId?: string | null
  focusComposer?: boolean
}): JSX.Element | null {
  const headingId = useId()
  const panelRef = useRef<HTMLElement>(null)
  const canCreate = allowedActions.includes('addPostNote')
  const frame = usePostNotesFrame({
    postId,
    channelId,
    canCreate,
    focusComposer,
    hidden,
    fallbackRef: panelRef,
  })

  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      id={id}
      hidden={hidden}
      className={styles.panel}
      aria-labelledby={headingId}
      aria-label="Post notes panel"
      data-testid="post-details-notes"
      data-collaboration-notes-panel=""
      data-post-id={postId}
    >
      <div className={styles.notes}>
        <PostNotesProvider {...frame.providerProps}>
          <PanelHeader
            headingId={headingId}
            infoButtonRef={frame.infoButtonRef}
          />
          <PostNotesFeed />
          {canCreate && <CreatePostNoteForm fieldRef={frame.createFieldRef} />}
          <FocusAfterDelete
            requested={frame.focusAfterDeleteRequested}
            onHandled={frame.clearFocusAfterDelete}
            focusCreate={frame.focusCreate}
          />
        </PostNotesProvider>
      </div>
    </aside>
  )
}

function PanelHeader({
  headingId,
  infoButtonRef,
}: {
  headingId: string
  infoButtonRef: { current: HTMLButtonElement | null }
}): JSX.Element | null {
  const { notes, create } = usePostNotesContext()
  const threadEmpty =
    notes.status === 'ready' &&
    notes.notes.length === 0 &&
    create.confirmedNotes.length === 0 &&
    !notes.hasNextPage
  if (threadEmpty) return null

  return (
    <header className={styles.header}>
      <div className={styles.titleBlock}>
        <h2 id={headingId} className={styles.title}>
          Notes
        </h2>
        <SplitTagBadge splitName={COLLABORATION_NOTES_SPLIT} />
        <NotesVisibilityHint buttonRef={infoButtonRef} />
      </div>
      <NotesFeedActions />
    </header>
  )
}
